import _cloneDeep from "lodash/cloneDeep";
import _get from "lodash/get";
import _isArray from "lodash/isArray";
import _isObject from "lodash/isObject";
import _set from "lodash/set";
import { Field } from "@js/invenio_rdm_records/src/deposit/serializers";
import {
  COMMUNITY_EXTENSION_METADATA_PATH,
  COMMUNITY_EXTENSION_SCHEMA_METADATA_PATH,
} from "./constants";

const stripInternalKeys = (value) => {
  if (_isArray(value)) {
    return value.map(stripInternalKeys);
  }

  if (_isObject(value) && value !== null) {
    return Object.entries(value).reduce((acc, [key, val]) => {
      // __key is added by ArrayField/GroupField internals.
      // *__autocomplete_id is a UI-only helper for RemoteSelectField.
      if (key === "__key" || key.endsWith("__autocomplete_id")) {
        return acc;
      }

      acc[key] = stripInternalKeys(val);
      return acc;
    }, {});
  }

  return value;
};

export class CustomField extends Field {
  constructor({
    fieldpath,
    deserializedDefault = null,
    serializedDefault = null,
    allowEmpty = false,
    vocabularyFields = [],
  }) {
    super({ fieldpath, deserializedDefault, serializedDefault, allowEmpty });
    this.vocabularyFields = vocabularyFields;
  }

  recursiveMapping(value, isVocabularyField, mapValue) {
    let _value = null;

    if (_isArray(value)) {
      _value = value.map((v, i) => mapValue(v, i, isVocabularyField));
    } else if (_isObject(value) && !isVocabularyField) {
      for (let key in value) {
        value[key] = this.recursiveMapping(
          value[key],
          isVocabularyField,
          mapValue
        );
      }
      _value = value;
    } else {
      _value = mapValue(value, null, isVocabularyField);
    }

    return _value;
  }

  #mapCustomFields(record, customFields, mapValue) {
    if (customFields !== null) {
      for (const [key, value] of Object.entries(customFields)) {
        const isVocabularyField = this.vocabularyFields.includes(key);
        const _value = this.recursiveMapping(
          value,
          isVocabularyField,
          mapValue
        );

        record = _set(
          record,
          `${COMMUNITY_EXTENSION_METADATA_PATH}.${key}`,
          value
        );
      }
    }
  }

  deserialize(record) {
    const _deserialize = (value, i = undefined, isVocabulary = false) => {
      // Do NOT collapse { id, schema, url, label } to only id here.
      // NestedVocabularyField reads value.id itself for vocabulary_object fields.

      if (i !== undefined && typeof value === "object" && value !== null) {
        value.__key = i;
      }

      return value;
    };

    const _record = _cloneDeep(record);
    const customFields = _get(record, this.fieldpath, this.deserializedDefault);
    this.#mapCustomFields(_record, customFields, _deserialize);
    return _record;
  }

  serialize(record) {
    const _serialize = (value, i = undefined, isVocabulary = false) => {
      // Keep old fallback for plain vocabulary fields that still submit strings.
      // This does not affect vocabulary_object values that are already objects.
      if (isVocabulary && typeof value === "string") {
        return { id: value };
      }

      if (typeof value === "object" && value !== null) {
        delete value.__key;
      }

      return value;
    };

    const _record = _cloneDeep(record);
    const customFields = _get(_record, this.fieldpath, this.serializedDefault);

    this.#mapCustomFields(_record, customFields, _serialize);

    const mappedCustomFields = _get(
      _record,
      COMMUNITY_EXTENSION_METADATA_PATH,
      this.serializedDefault
    );

    _set(
      _record,
      COMMUNITY_EXTENSION_METADATA_PATH,
      stripInternalKeys(mappedCustomFields)
    );

    return _record;
  }
}

export const communityExtensionSerializerFields = (
  customFieldVocabularies = []
) => ({
  community_extension: new CustomField({
    fieldpath: COMMUNITY_EXTENSION_METADATA_PATH,
    deserializedDefault: {},
    serializedDefault: {},
    vocabularyFields: customFieldVocabularies,
  }),
  community_extension_schema: new Field({
    fieldpath: COMMUNITY_EXTENSION_SCHEMA_METADATA_PATH,
    deserializedDefault: "",
    serializedDefault: "",
  }),
});
